import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import type { CancelBookingReservationFormValues } from '@/types/schemas';

import { Form, typedFields } from '@/components/Form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  CANCELLATION_REASON_MAX_LENGTH,
  createCancelBookingReservationSchema,
} from '@/types/schemas';

const formFields = typedFields<CancelBookingReservationFormValues>();

type CancelBookingReasonDialogProps = {
  title: string;
  description: string;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (cancellationReason: string) => void;
};

export const CancelBookingReasonDialog = (
  props: CancelBookingReasonDialogProps,
) => {
  const { title, description, isPending, onClose, onConfirm } = props;
  const { t } = useTranslation();

  const form = useForm<CancelBookingReservationFormValues>({
    resolver: zodResolver(createCancelBookingReservationSchema(t)),
    mode: 'onTouched',
    defaultValues: { cancellation_reason: '' },
  });

  const cancellationReason = useWatch({
    control: form.control,
    name: 'cancellation_reason',
  });
  const isReasonMissing = cancellationReason.trim() === '';

  const onSubmit = form.handleSubmit((values) =>
    onConfirm(values.cancellation_reason),
  );

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => !isOpen && !isPending && onClose()}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Form
          form={form}
          onSubmit={onSubmit}
        >
          <formFields.Textarea
            name="cancellation_reason"
            label={t('Cancellation reason')}
            isRequired
            rows={4}
            textareaProps={{
              maxLength: CANCELLATION_REASON_MAX_LENGTH,
              autoFocus: true,
            }}
          />
        </Form>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            {t('Close')}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isPending || isReasonMissing}
          >
            {t('Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
